import React, { useRef, useState, useEffect, useReducer, forwardRef, useImperativeHandle, useCallback } from "react";
import "./ChatBox.css";
import type { JSX } from "react";
import { isIOS } from "../../../utils/iosDetection";
import CLINIC_CONFIG from '../../../config/clinicConfig';

// ★ Sub-components
import StreamingMessage from './StreamingMessage';
import TypingBubble from './TypingBubble';
import VoiceCallScreen from './VoiceCallScreen';

// ★ Types & utilities
import type { PendingImageData, ChatboxProps, ChatboxRef, ChatState, ChatAction } from './chatTypes';
import { chatTips } from './chatTypes';
import { useChatWebSocket, generateSessionId } from './useChatWebSocket';
import { useVoiceCall } from './useVoiceCall';

// ─── Utilities ───────────────────────────────────────────────────────

/** Format text: bold, linkify emails/phones */
const formatText = (input: string): string => {
  // Bold: **text** → <strong>
  input = input.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Email links
  input = input.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="mailto:$1" class="chat-link chat-link-email">$1</a>'
  );
  // Phone links
  input = input.replace(
    /(\+?\d{1,3}[\s-]?)?(\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4})/g,
    (match) => {
      const number = match.replace(/[^\d+]/g, '');
      return `<a href="tel:${number}" class="chat-link chat-link-phone">${match}</a>`;
    }
  );
  return input;
};

/** Strip leading markdown bullet markers (* or -) from a line */
const cleanListItem = (item: string): string => item.replace(/^[\*\-]\s+/, '').trim();

/** Format bot replies: bold, linkified emails/phones, bullet lists */
const formatBotReply = (text: string): JSX.Element => {
  if (text.includes("\n") || text.includes("•")) {
    const items = text.split(/\n|•/)
      .map(item => cleanListItem(item.trim()))
      .filter(item => item.length > 0);
    return (
      <ul className="list-unstyled ms-3">
        {items.map((item, idx) => (
          <li key={idx} className="mb-1" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
        ))}
      </ul>
    );
  }

  return <p className="mb-0" dangerouslySetInnerHTML={{ __html: formatText(text) }} />;
};

/** Extract quick-reply options from bot text */
const getQuickReplies = (text: string): string[] => {
  const replies: string[] = [];
  const lines = text.split('\n');
  const meaningful = lines.map(l => l.trim()).filter(l => l.length > 0);
  const lastLine = (meaningful[meaningful.length - 1] || '').replace(/\*\*/g, '');

  // 1. Detect "X or Y?" choices in the last line (high priority)
  if (lastLine.endsWith('?') && /\bor\b/i.test(lastLine)) {
    const orParts = lastLine.split(/,?\s+or\s+/i);
    if (orParts.length === 2) {
      const extractChoice = (part: string): string => {
        part = part.replace(/\??$/, '').trim();
        part = part.replace(/^(?:also,?\s+)?(?:are you|is this|do you have|would (?:this|that|it) be|would you (?:like|prefer)|have you|will this be|will you be)\s+/i, '');
        part = part.replace(/^(?:a|an|the|your|our|my)\s+/i, '');
        part = part.replace(/\s+(?:at|in|with|from|to)\s+(?:our|the|this|a|an)\s+.*$/i, '');
        return part.trim();
      };
      const c1 = extractChoice(orParts[0]);
      const c2 = extractChoice(orParts[1]);
      if (c1.length > 0 && c1.length < 40) replies.push(c1.charAt(0).toUpperCase() + c1.slice(1));
      if (c2.length > 0 && c2.length < 40) replies.push(c2.charAt(0).toUpperCase() + c2.slice(1));
    }
  }

  // 2. Extract examples from parenthetical hints: (For example: X, Y, Z)
  const exampleRegex = /\((?:For example|e\.g\.|such as|like)[:\s]+([^)]+)\)/gi;
  let exMatch;
  while ((exMatch = exampleRegex.exec(text)) !== null) {
    const examples = exMatch[1]
      .replace(/\betc\.?\b/gi, '')
      .replace(/\band\s+/gi, ', ')
      .split(/,\s*/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && e.length < 30)
      .map(e => e.charAt(0).toUpperCase() + e.slice(1));
    replies.push(...examples);
  }

  if (replies.length > 0) return replies.slice(0, 6);

  // 3. Numbered options: "1. option" or "1) option"
  const numbered: string[] = [];
  for (const line of lines) {
    const trimmed = line.replace(/^[\*\-•]\s*/, '').trim();
    const match = trimmed.match(/^\d+[.)]\s+(.+)/);
    if (match) numbered.push(match[1].replace(/\*\*/g, '').replace(/\s*\(.*?\)\s*/g, '').trim());
  }
  if (numbered.length >= 2) return numbered.slice(0, 6);

  // 4. Yes/no question patterns
  const cleanLast = lastLine.toLowerCase();
  if (!cleanLast.endsWith('?')) return [];

  const yesNoPatterns = [
    'would you like', 'do you want', 'shall i', 'can i', 'should i',
    'do you need', 'are you interested', 'would you prefer',
    'is there anything', 'do you have any', 'anything else',
    'like to', 'want to', 'need help', 'help with',
    'ready to', 'like me to', 'may i', 'could i',
  ];
  if (yesNoPatterns.some(p => cleanLast.includes(p))) return ['Yes', 'No'];

  return [];
};

/** Quick action button definitions */
const QUICK_ACTIONS = [
  { icon: 'fa-regular fa-calendar-check', label: 'Schedule Appointment', text: 'I want to schedule an appointment' },
  { icon: 'fa-solid fa-dollar-sign', label: 'Treatment Costs', text: 'What are the treatment costs?' },
  { icon: 'fa-solid fa-credit-card', label: 'Payment & Financing', text: 'What are your payment and financing options?' },
  { icon: 'fa-solid fa-shield-halved', label: 'Insurance Coverage', text: 'Can you check my insurance coverage?' },
  { icon: 'fa-solid fa-clock', label: 'Hours & Contact', text: 'What are your hours of operation and contact info?' },
];

/** Convert a File to a base64 data URL */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Failed to convert file to base64'));
    };
    reader.onerror = (error) => reject(error);
  });
};

// ─── Reducer ─────────────────────────────────────────────────────────

const initialState: ChatState = {
  phase: "idle",
  messages: [],
  thinkingSteps: [],
  streamingText: "",
};

// Note: thinkingSteps kept in state for type compatibility but no longer rendered

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "ADD_USER_MESSAGE":
      return {
        ...state,
        // Immediately go to "thinking" phase — shows typing indicator
        phase: "thinking",
        thinkingSteps: [],
        streamingText: "",
        messages: [...state.messages, { type: "user", text: action.text, timestamp: new Date() }],
      };

    case "ADD_TYPING":
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            type: "typing",
            text: action.text,
            timestamp: new Date(),
            extraInfo: { suggestion: action.suggestion },
          },
        ],
      };

    case "THINKING":
      return {
        ...state,
        phase: "thinking",
        // Append this thinking step to the list
        thinkingSteps: [...state.thinkingSteps, action.content],
      };

    case "CHUNK":
      return {
        ...state,
        phase: "streaming",
        streamingText: state.streamingText + action.content,
      };

    case "COMPLETE":
      return {
        ...state,
        phase: "idle",
        streamingText: "",
        // Keep thinkingSteps so user can still review them, but add final bot message
        messages: [
          ...state.messages.filter(m => m.type !== "typing"),
          // If there were thinking steps, add a thinking message that the panel can render
          ...(state.thinkingSteps.length > 0 ? [{
            type: "bot" as const,
            text: action.content,
            timestamp: new Date(),
            extraInfo: { suggestion: state.thinkingSteps },
          }] : [{
            type: "bot" as const,
            text: action.content,
            timestamp: new Date(),
          }]),
        ],
        thinkingSteps: [],
      };

    case "ADD_BOT_MESSAGE":
      return {
        ...state,
        phase: "idle",
        thinkingSteps: [],
        streamingText: "",
        messages: [...state.messages.filter(m => m.type !== "typing"), action.message],
      };

    case "ADD_IMAGE_MESSAGE":
      return {
        ...state,
        messages: [
          ...state.messages,
          { type: "image", text: action.text, timestamp: new Date(), imageUrl: action.imageUrl },
        ],
      };

    case "REMOVE_TYPING":
      return {
        ...state,
        messages: state.messages.filter(m => m.type !== "typing"),
      };

    case "ERROR":
      return {
        ...state,
        phase: "idle",
        thinkingSteps: [],
        streamingText: "",
        messages: [
          ...state.messages.filter(m => m.type !== "typing"),
          { type: "error", text: action.text, timestamp: new Date() },
        ],
      };

    case "SET_MESSAGES":
      return { ...state, messages: action.messages };

    case "CLEAR":
      return initialState;

    default:
      return state;
  }
}

// ─── Chatbox Component ──────────────────────────────────────────────

const Chatbox = forwardRef<ChatboxRef, ChatboxProps>(({ initialMessage, pendingMessage, pendingImages, onMessageProcessed, startVoiceCall, onVoiceCallStarted }, ref) => {
  const [inputValue, setInputValue] = useState("");
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const [sessionId, setSessionId] = useState("");
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  // Image upload state
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processedPendingMessageRef = useRef<string | null>(null);

  const CLINIC_ID = CLINIC_CONFIG.clinicId;

  // ── WebSocket hook (with streaming callbacks) ──────────────────────

  const {
    connectionStatus,
    isLoading,
    sendMessageViaWebSocket,
    handleStartTyping,
    setIsLoading,
  } = useChatWebSocket({
    sessionId,

    // Legacy single-shot response
    onBotMessage: (botMessage) => {
      dispatch({ type: "ADD_BOT_MESSAGE", message: botMessage });
    },

    // ── Streaming callbacks ──
    onThinking: (content) => {
      dispatch({ type: "THINKING", content });
    },
    onChunk: (content) => {
      dispatch({ type: "CHUNK", content });
    },
    onComplete: (content) => {
      dispatch({ type: "COMPLETE", content });
    },

    onSendError: () => {
      dispatch({
        type: "ERROR",
        text: "Sorry, I'm having trouble connecting. Please try again later.",
      });
    },
  });

  // ── Voice call hook ───────────────────────────────────────────────

  const {
    callState,
    isMuted,
    callDuration,
    transcript,
    startCall,
    endCall,
    toggleMute,
    error: voiceError,
  } = useVoiceCall();

  const isInCall = callState === 'active' || callState === 'connecting' || callState === 'ending';
  const hasMessages = state.messages.length > 0;

  // Auto-start voice call when opened from the floating nav voice button
  const voiceCallTriggeredRef = useRef(false);
  useEffect(() => {
    if (startVoiceCall && !voiceCallTriggeredRef.current && callState === 'idle') {
      voiceCallTriggeredRef.current = true;
      startCall();
      onVoiceCallStarted?.();
    }
    if (!startVoiceCall) {
      voiceCallTriggeredRef.current = false;
    }
  }, [startVoiceCall, callState, startCall, onVoiceCallStarted]);

  // ── File handling ─────────────────────────────────────────────────

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;
    const validFiles: File[] = [];
    const previewUrls: string[] = [];

    for (let i = 0; i < Math.min(files.length, 2); i++) {
      const file = files[i];

      if (!allowedTypes.includes(file.type)) {
        dispatch({
          type: "ADD_BOT_MESSAGE",
          message: {
            type: 'bot',
            text: `⚠️ File "${file.name}" is not supported. Please upload JPG, PNG, GIF images or PDF documents.`,
            timestamp: new Date(),
          },
        });
        continue;
      }

      if (file.size > maxSize) {
        dispatch({
          type: "ADD_BOT_MESSAGE",
          message: {
            type: 'bot',
            text: `⚠️ File "${file.name}" is too large. Maximum file size is 10MB.`,
            timestamp: new Date(),
          },
        });
        continue;
      }

      validFiles.push(file);
      previewUrls.push(file.type === 'application/pdf' ? 'pdf' : URL.createObjectURL(file));
    }

    if (validFiles.length > 0) {
      setSelectedImages(validFiles);
      setImagePreviewUrls(previewUrls);
    }

    event.target.value = '';
  }, []);

  const handleInsuranceUpload = useCallback(async () => {
    if (selectedImages.length === 0) return;
    setIsUploadingImage(true);

    dispatch({
      type: "ADD_IMAGE_MESSAGE",
      text: `📄 Uploading insurance card${selectedImages.length > 1 ? 's' : ''}...`,
      imageUrl: imagePreviewUrls[0] !== 'pdf' ? imagePreviewUrls[0] : undefined,
    });

    dispatch({ type: "ADD_TYPING", text: "Tooth Fairy is reading your insurance card..." });

    try {
      const base64Images = await Promise.all(selectedImages.map(file => fileToBase64(file)));
      const apiUrl = `https://apig.todaysdentalinsights.com/ai-agents/public/clinic/${CLINIC_ID}/insurance/extract`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images: base64Images.map(base64 => ({ base64, type: 'image/jpeg' })),
          clinicId: CLINIC_ID,
        }),
      });

      const result = await response.json();
      dispatch({ type: "REMOVE_TYPING" });

      if (response.ok && result.success) {
        dispatch({
          type: "ADD_BOT_MESSAGE",
          message: {
            type: 'bot',
            text: result.chatbotMessage || 'I found the following information from your insurance card.',
            timestamp: new Date(),
            extraInfo: { insuranceInfo: result.extractedInfo },
          },
        });
      } else {
        dispatch({
          type: "ADD_BOT_MESSAGE",
          message: {
            type: 'bot',
            text: result.message || 'Sorry, I had trouble reading your insurance card. Please make sure the image is clear and try again, or type your insurance information instead.',
            timestamp: new Date(),
          },
        });
      }
    } catch (error) {
      console.error('Error uploading insurance card:', error);
      dispatch({ type: "REMOVE_TYPING" });
      dispatch({
        type: "ADD_BOT_MESSAGE",
        message: {
          type: 'bot',
          text: 'Sorry, there was an error processing your insurance card. Please try again or type your insurance information instead.',
          timestamp: new Date(),
        },
      });
    } finally {
      setIsUploadingImage(false);
      setSelectedImages([]);
      imagePreviewUrls.forEach(url => { if (url !== 'pdf') URL.revokeObjectURL(url); });
      setImagePreviewUrls([]);
    }
  }, [selectedImages, imagePreviewUrls, CLINIC_ID]);

  const handleImageButtonClick = () => fileInputRef.current?.click();

  // Auto-upload when images are selected
  useEffect(() => {
    if (selectedImages.length > 0) handleInsuranceUpload();
  }, [selectedImages, handleInsuranceUpload]);

  // ── Message sending ───────────────────────────────────────────────

  const sendMessage = useCallback(async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed) return;

    // Add user message + set phase to "thinking" (shows typing indicator immediately)
    dispatch({ type: "ADD_USER_MESSAGE", text: trimmed });

    if (trimmed.toLowerCase() === "bye") {
      setTimeout(() => {
        const modalEl = document.getElementById("chatModal");
        if (modalEl) {
          const modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);
          modalInstance?.hide();
        }
      }, 5000);
    }

    setIsLoading(true);
    // Always call sendMessageViaWebSocket — it handles pending message
    // queuing and connection triggering internally when WS is not yet open
    sendMessageViaWebSocket(trimmed);
  }, [sendMessageViaWebSocket, setIsLoading]);

  // ── Initialization and lifecycle ──────────────────────────────────

  useEffect(() => {
    setSessionId(generateSessionId());
    dispatch({ type: "CLEAR" });
    setIsIOSDevice(isIOS());
  }, []);

  // ── iOS viewport handling (keyboard open/close) ──────────────────
  useEffect(() => {
    if (!isIOSDevice) return;

    const wrapper = chatRef.current?.closest('.chatbox-wrapper') as HTMLElement | null;
    if (!wrapper) return;

    const updateHeight = () => {
      const vh = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      wrapper.style.setProperty('--ios-vh', `${vh}px`);
    };

    updateHeight();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', updateHeight);
    } else {
      window.addEventListener('resize', updateHeight);
    }

    const handleFocus = (e: FocusEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === 'INPUT') {
        setTimeout(() => window.scrollTo(0, 0), 0);
        updateHeight();
      }
    };
    document.addEventListener('focusin', handleFocus);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateHeight);
      } else {
        window.removeEventListener('resize', updateHeight);
      }
      document.removeEventListener('focusin', handleFocus);
      wrapper.style.removeProperty('--ios-vh');
    };
  }, [isIOSDevice]);

  // Rotating placeholder
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => prev === chatTips.length - 1 ? 0 : prev + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Handle pending messages from external sources
  useEffect(() => {
    if (pendingMessage && pendingMessage.trim() && processedPendingMessageRef.current !== pendingMessage) {
      processedPendingMessageRef.current = pendingMessage;
      if (!pendingImages || pendingImages.length === 0) sendMessage(pendingMessage);
      onMessageProcessed?.();
    }
    return () => {
      if (!pendingMessage || !pendingMessage.trim()) processedPendingMessageRef.current = null;
    };
  }, [pendingMessage, pendingImages, onMessageProcessed, sendMessage]);

  // Handle pending images from hero panel
  useEffect(() => {
    if (pendingImages && pendingImages.length > 0) {
      setSelectedImages(pendingImages.map(img => img.file));
      setImagePreviewUrls(pendingImages.map(img => img.previewUrl === 'pdf' ? '' : img.previewUrl));
    }
  }, [pendingImages]);

  // Expose sendMessage via ref
  useImperativeHandle(ref, () => ({
    sendMessage: (message: string, images?: PendingImageData[]) => {
      if (images && images.length > 0) {
        setSelectedImages(images.map(img => img.file));
        setImagePreviewUrls(images.map(img => img.previewUrl === 'pdf' ? '' : img.previewUrl));
      }
      sendMessage(message);
    }
  }), [sendMessage]);

  // Send initial message
  const sendInitialMessageToLex = useCallback(async (message: string) => {
    if (!sessionId) return;
    setIsLoading(true);
    sendMessageViaWebSocket(message);
  }, [sessionId, sendMessageViaWebSocket, setIsLoading]);

  useEffect(() => {
    if (initialMessage && initialMessage.trim() && sessionId) {
      dispatch({ type: "ADD_USER_MESSAGE", text: initialMessage.trim() });
      sendInitialMessageToLex(initialMessage.trim());
    }
  }, [initialMessage, sessionId, sendInitialMessageToLex]);

  // Auto-scroll
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [state.messages, state.streamingText, state.thinkingSteps, state.phase]);

  // Haptic feedback
  const triggerVibration = useCallback((pattern: number | number[] = 30) => {
    try {
      if (typeof navigator !== 'undefined' && typeof (navigator as any).vibrate === 'function') {
        (navigator as any).vibrate(pattern);
      }
    } catch { }
  }, []);

  const handleSendMessage = async (externalMessage?: string) => {
    const message = externalMessage || inputValue;
    if (!externalMessage) triggerVibration(35);
    await sendMessage(message);
    if (!externalMessage) setInputValue("");
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="chatbox-wrapper d-flex flex-column h-100 w-100" style={{ position: 'relative' }}>
      {/* Fullscreen voice call overlay */}
      {isInCall && (
        <VoiceCallScreen
          callState={callState}
          isMuted={isMuted}
          callDuration={callDuration}
          transcript={transcript}
          onEndCall={endCall}
          onToggleMute={toggleMute}
          error={voiceError}
          onRetry={() => startCall()}
        />
      )}

      <div className="chatbox-body flex-grow-1 overflow-auto" ref={chatRef}>
        {/* Welcome quick actions — shown when chat is empty */}
        {!hasMessages && state.phase === 'idle' && (
          <div className="quick-actions-welcome">
            <img src="/images/favicon.webp" alt="Tooth Fairy" className="quick-actions-logo" />
            <div className="quick-actions-greeting">How can I help you today?</div>
            <div className="quick-actions-grid">
              {QUICK_ACTIONS.map((action, idx) => (
                <button
                  key={idx}
                  className="quick-action-card"
                  onClick={() => handleSendMessage(action.text)}
                  disabled={isLoading}
                >
                  <i className={action.icon}></i>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {state.messages.map((msg, i) => {
          // ── Error message ──
          if (msg.type === "error") {
            return (
              <div key={i} className="message mb-2 justify-content-start d-flex">
                <div className="bubble bot-bubble error-bubble">
                  <span className="error-icon">⚠️</span> {msg.text}
                </div>
              </div>
            );
          }

          return (
            <div
              key={i}
              className={`message mb-2 ${msg.type === "user" || msg.type === "image" ? "justify-content-end" : "justify-content-start"} d-flex`}
            >
              <div className={`bubble ${msg.type === "user" || msg.type === "image" ? "user-bubble" : "bot-bubble"}`}>
                {/* Image message with preview */}
                {msg.type === "image" && msg.imageUrl && (
                  <div className="mb-2">
                    <img src={msg.imageUrl} alt="Insurance card" style={{ maxWidth: 150, maxHeight: 100, borderRadius: 8, objectFit: 'cover' }} />
                  </div>
                )}

                {/* Typing indicator (used only for insurance upload typing) */}
                {msg.type === "typing" ? (
                  <div className="typing-indicator-wrap">
                    <TypingBubble />
                    <span className="typing-label">{msg.text}</span>
                  </div>
                ) : msg.type === "bot" ? (
                  <>
                    {formatBotReply(msg.text)}
                    {/* Quick reply buttons — only on the last bot message when idle */}
                    {i === state.messages.length - 1 && state.phase === "idle" && !isLoading && (() => {
                      const replies = getQuickReplies(msg.text);
                      if (replies.length === 0) return null;
                      return (
                        <div className="quick-reply-options">
                          {replies.map((reply, idx) => (
                            <button
                              key={idx}
                              className="quick-reply-btn"
                              onClick={() => handleSendMessage(reply)}
                            >
                              {reply}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  msg.text
                )}

                {/* Insurance info display */}
                {msg.type === "bot" && msg.extraInfo?.insuranceInfo && (
                  <div className="insurance-info mt-2 p-2 bg-light rounded" style={{ fontSize: '0.85rem' }}>
                    <div className="fw-bold text-primary mb-1">
                      <i className="bi bi-shield-check me-1"></i>Insurance Details
                    </div>
                    {msg.extraInfo.insuranceInfo.insuranceCompany && (
                      <div><strong>Company:</strong> {msg.extraInfo.insuranceInfo.insuranceCompany}</div>
                    )}
                    {msg.extraInfo.insuranceInfo.groupNumber && (
                      <div><strong>Group #:</strong> {msg.extraInfo.insuranceInfo.groupNumber}</div>
                    )}
                    {msg.extraInfo.insuranceInfo.memberId && (
                      <div><strong>Member ID:</strong> {msg.extraInfo.insuranceInfo.memberId}</div>
                    )}
                    {msg.extraInfo.insuranceInfo.planType && (
                      <div><strong>Plan:</strong> {msg.extraInfo.insuranceInfo.planType}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* ── Active typing indicator (shown during thinking/streaming phases) ── */}
        {state.phase === "thinking" && (
          <div className="message mb-2 justify-content-start d-flex">
            <div className="bubble bot-bubble">
              <TypingBubble />
            </div>
          </div>
        )}

        {/* ── StreamingMessage (replaces typing indicator during streaming) ── */}
        {state.phase === "streaming" && state.streamingText && (
          <StreamingMessage
            text={state.streamingText}
            isStreaming={true}
          />
        )}
      </div>

      {/* Collapsed quick actions strip — shown when conversation is active */}
      {hasMessages && !isInCall && (
        <div className="quick-actions-strip">
          {quickActionsOpen ? (
            <div className="quick-actions-expanded-strip">
              <div className="quick-actions-strip-header">
                <span className="quick-actions-strip-label">Quick Actions</span>
                <button className="quick-actions-collapse-btn" onClick={() => setQuickActionsOpen(false)}>
                  <i className="bi bi-chevron-down"></i>
                </button>
              </div>
              <div className="quick-actions-strip-list">
                {QUICK_ACTIONS.map((action, idx) => (
                  <button
                    key={idx}
                    className="quick-action-strip-btn"
                    onClick={() => { setQuickActionsOpen(false); handleSendMessage(action.text); }}
                    disabled={isLoading}
                  >
                    <i className={`bi ${action.icon} me-2`}></i>
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="quick-actions-icon-row">
              {QUICK_ACTIONS.map((action, idx) => (
                <button
                  key={idx}
                  className="quick-action-icon-btn"
                  onClick={() => handleSendMessage(action.text)}
                  title={action.label}
                  disabled={isLoading}
                >
                  <i className={action.icon}></i>
                </button>
              ))}
              <button
                className="quick-action-icon-btn quick-action-expand"
                onClick={() => setQuickActionsOpen(true)}
                title="Show all options"
              >
                <i className="bi bi-chevron-up"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/bmp,application/pdf"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-label="Upload insurance card image or PDF"
      />

      {/* Image/PDF preview bar */}
      {imagePreviewUrls.length > 0 && (
        <div className="p-2 border-top bg-light d-flex align-items-center gap-2">
          {imagePreviewUrls.map((url, idx) => (
            <div key={idx} className="position-relative">
              {url === 'pdf' ? (
                <div style={{ width: 50, height: 50, borderRadius: 4, border: '2px solid #dc3545', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 'bold', color: '#dc3545' }}>
                  PDF
                </div>
              ) : (
                <img src={url} alt={`Preview ${idx + 1}`} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, border: '2px solid #007bff' }} />
              )}
            </div>
          ))}
          <span className="text-muted small">Processing...</span>
        </div>
      )}

      {/* Chat input footer — hidden during voice call (fullscreen overlay handles it) */}
      {!isInCall && (
        <div
          className="chatbox-footer border-top d-flex align-items-center"
          style={{ position: 'relative', zIndex: 10, background: isIOSDevice ? '#ffffff' : undefined }}
        >
          <button
            className="btn chatbox-upload-btn"
            onClick={handleImageButtonClick}
            disabled={isLoading || isUploadingImage}
            aria-label="Upload insurance card"
            title="Upload insurance card (image or PDF)"
          >
            {isUploadingImage ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-upload"></i>
            )}
          </button>

          <input
            ref={inputRef}
            className="form-control chatbox-input"
            placeholder={connectionStatus === 'connecting' ? "Connecting..." : chatTips[placeholderIndex]}
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); handleStartTyping(); }}
            onFocus={handleStartTyping}
            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
            disabled={isLoading}
            style={{ fontSize: isIOSDevice ? '16px' : '1rem' }}
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
          />

          <button
            className="btn chatbox-voice-btn"
            onClick={() => startCall()}
            disabled={isLoading}
            aria-label="Talk to ToothFairy"
            title="Talk to ToothFairy"
          >
            <i className="bi bi-person-fill"></i>
          </button>

          <button
            className="btn chatbox-send-btn"
            onClick={() => handleSendMessage()}
            disabled={!inputValue.trim() || isLoading}
            aria-label="Send"
          >
            {isLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fa-solid fa-paper-plane"></i>
            )}
          </button>
        </div>
      )}
    </div>
  );
});

Chatbox.displayName = 'Chatbox';

export default Chatbox;
